/**
 * Phase 3A platform isolation.
 *
 * Distinguishes three runtimes the frontend can find itself in:
 *  - Browser/PWA (not Tauri at all)
 *  - Generic Tauri runtime (any OS, including Android)
 *  - Windows native Tauri runtime specifically
 *
 * `isTauriRuntime()` alone is NOT sufficient to gate Windows-only native
 * behaviors (native clipboard watcher/write, tray, global shortcut,
 * Windows Phase 2D offline queue eligibility) because Android is also a
 * Tauri runtime. Those behaviors must only run when `isWindowsNative()` is
 * true, which requires the async `resolvePlatform()` round trip to the
 * Rust `get_platform` command to have completed. Before it resolves (or if
 * it fails/is unavailable), `isWindowsNative()` safely returns false --
 * this is a fail-safe default, never a fail-open one.
 */

export const isTauriRuntime = (): boolean =>
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

let cachedPlatform: string = "unknown";

/** Test-only escape hatch to reset/seed the module-level cache. */
export function __setCachedPlatformForTests(value: string): void {
  cachedPlatform = value;
}

export function getCachedPlatform(): string {
  return cachedPlatform;
}

/** Resolves and caches the native platform. No-op outside a Tauri runtime. */
export async function resolvePlatform(): Promise<string> {
  if (!isTauriRuntime()) return cachedPlatform;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    cachedPlatform = await invoke<string>("get_platform");
  } catch {
    // Leave cachedPlatform as-is -- treated as non-Windows-native (safe default).
  }
  return cachedPlatform;
}

/** True only for the Windows native Tauri runtime. False for Android native,
 * any other Tauri runtime, Browser/PWA, and while resolution is pending. */
export const isWindowsNativeRuntime = (): boolean =>
  isTauriRuntime() && cachedPlatform === "windows";
